// components/AddBoxDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useCreateBox } from '../hooks/useCreateBox';

export default function AddBoxDialog({ open, onClose, onBoxAdded }) {
  const [formData, setFormData] = useState({
    box_id: '',
    title: '',
    description: '',
    location: 'UCS'
  });

  const { createBox, loading, error, clearError } = useCreateBox();

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (error) clearError();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const newBox = await createBox(formData);

      // Reset form
      setFormData({
        box_id: '',
        title: '',
        description: '',
        location: 'UCS'
      });

      // Call the callback to refresh the boxes list
      if (onBoxAdded) {
        onBoxAdded(newBox);
      }

      // Close dialog
      onClose();
    } catch (err) {
      // Error is already handled by the hook
      console.error('Failed to create box:', err);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFormData({
        box_id: '',
        title: '',
        description: '',
        location: 'UCS'
      });
      clearError();
      onClose();
    }
  };

  // Clear error when dialog closes
  useEffect(() => {
    if (!open) {
      clearError();
    }
  }, [open, clearError]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="h2">
          Add New Monitoring Box
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Box ID"
            value={formData.box_id}
            onChange={handleInputChange('box_id')}
            required
            fullWidth
            disabled={loading}
            placeholder="Enter unique box identifier"
            helperText="This will be the unique identifier for your monitoring box"
          />

          <TextField
            label="Title"
            value={formData.title}
            onChange={handleInputChange('title')}
            required
            fullWidth
            disabled={loading}
            placeholder="Enter a descriptive title"
            helperText="A human-readable name for this box"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            placeholder="Enter optional description"
            helperText="Optional description of the box's purpose or configuration"
          />

          <FormControl fullWidth disabled={loading}>
            <InputLabel>Location</InputLabel>
            <Select
              value={formData.location}
              onChange={handleInputChange('location')}
              label="Location"
            >
              <MenuItem value="UCS">UCS</MenuItem>
              <MenuItem value="Building A">Building A</MenuItem>
              <MenuItem value="Building B">Building B</MenuItem>
              <MenuItem value="Outdoor">Outdoor</MenuItem>
              <MenuItem value="Laboratory">Laboratory</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !formData.box_id.trim() || !formData.title.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Box'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}